import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { asEur, asPercent } from "../utility/numbers";

export interface BlobLane {
  title: string;
  begin: number;
  length: number;
  values: number[];
  commitment: number;
  called: number;
}

interface Props {
  headings: string[];
  lanes: BlobLane[];
  showTotals: boolean;
}

interface TooltipProps {
  visible: boolean;
  fund: string;
  heading: string;
  call: number;
  called: number;
  commitment: number;
  locationX: number;
  locationY: number;
}

const tooltipInitialData: TooltipProps = {
  visible: false,
  fund: "",
  heading: "",
  call: 0,
  called: 0,
  commitment: 0,
  locationX: 0,
  locationY: 0,
};

const BlobTooltip = (props: TooltipProps) => {
  const barChartWidth = 200;
  const barChartHeight = 20;
  const almostFull = (props.called + props.call) / props.commitment > 0.85;

  return (
    <div
      id="blobTooltip"
      style={{
        left: `${props.locationX}px`,
        top: `${props.locationY}px`,
        display: props.visible ? "block" : "none",
      }}
    >
      <table style={{ marginBottom: 10 }}>
        <tbody>
          <tr>
            <td>
              <b>{props.fund}</b>
            </td>
            <td>{props.heading}</td>
          </tr>
          <tr>
            <td>Kutsu tänä vuonna:</td>
            <td>{asEur(props.call)}</td>
          </tr>
          <tr>
            <td>Kutsuttu yhteensä:</td>
            <td>{asEur(props.called)}</td>
          </tr>
          <tr>
            <td>Sitoumus:</td>
            <td>{asEur(props.commitment)}</td>
          </tr>
        </tbody>
      </table>
      <svg
        className="tooltipChart"
        width={`${barChartWidth}px`}
        height={`${barChartHeight}px`}
        viewBox={`0 0 ${barChartWidth} ${barChartHeight}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={barChartWidth} height={barChartHeight} fill="#D1D1D1" />
        <rect
          width={(props.called * barChartWidth) / props.commitment || 0}
          height={barChartHeight}
          fill="#000033"
        />
        <rect
          x={(props.called * barChartWidth) / props.commitment || 0}
          width={(props.call * barChartWidth) / props.commitment || 0}
          height={barChartHeight}
          fill="#4A76A2"
        />
        <text
          width="20"
          height={barChartHeight}
          fill={almostFull ? "white" : "#666"}
          textAnchor={almostFull ? "end" : "begin"}
          x={
            almostFull
              ? barChartWidth - 5
              : ((props.called + props.call) * barChartWidth) /
                  props.commitment +
                  3 || 0
          }
          y={barChartHeight - 5}
        >
          {asPercent(((props.called + props.call) * 100) / props.commitment, 0)}
        </text>
      </svg>
    </div>
  );
};

const BlobTable = (props: Props) => {
  const [tooltipData, setTooltipData] =
    useState<TooltipProps>(tooltipInitialData);

  const maxBlobRadius = 9;
  const minBlobRadius = 2;
  const maxSumBarHeight = 30;

  const { t } = useTranslation();

  const noData = props.lanes.length === 0;

  const maxCall = !noData
    ? Math.max(...props.lanes.map((lane) => Math.max(...lane.values)))
    : 0;

  const columnSums = !noData
    ? props.lanes[0].values.map((_, index) =>
        props.lanes.map((lane) => lane.values[index]).reduce((a, b) => a + b)
      )
    : [];

  const maxSum = !noData ? Math.max(...columnSums) : 0;

  const engageTooltip = (
    event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    fund: string,
    heading: string,
    call: number,
    called: number,
    commitment: number
  ) => {
    setTooltipData({
      visible: true,
      fund,
      heading,
      call,
      called,
      commitment,
      locationX: event.clientX + 10,
      locationY: event.clientY - 150,
    });
  };

  return (
    <div id="blobTableArea">
      <table className={"blobTable"}>
        <thead>
          <tr>
            <th>{t("fund")}</th>
            {props.headings.map((heading, index) => (
              <th key={index}>{heading}</th>
            ))}
          </tr>
        </thead>
        {noData ? (
          <tbody>
            <tr>
              <td className="noData" colSpan={12}>
                {t("no_data")}
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {props.lanes
              .sort((a, b) => a.begin - b.begin)
              .map((lane, index) => (
                <tr className={"blobLane"} key={index}>
                  <th scope={"row"}>{lane.title}</th>
                  {lane.values.map((sum, col) => (
                    <td
                      className={"blobCell"}
                      onMouseEnter={(ev) =>
                        engageTooltip(
                          ev,
                          lane.title,
                          props.headings[col],
                          sum,
                          lane.called,
                          lane.commitment
                        )
                      }
                      onMouseLeave={() => {
                        setTooltipData(tooltipInitialData);
                      }}
                      key={col}
                    >
                      <svg
                        className={"blobGraphic"}
                        viewBox="20 0 30 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {lane.begin < col &&
                          lane.begin + lane.length >= col && (
                            <rect
                              className={"precedent"}
                              x="-15"
                              y="8"
                              width="53"
                              height="4"
                              rx="2"
                              fill="#A9D4FF"
                            />
                          )}
                        {lane.begin <= col &&
                          lane.begin + lane.length > col && (
                            <rect
                              className={"following"}
                              x="34"
                              y="8"
                              width="53"
                              height="4"
                              rx="2"
                              fill="#A9D4FF"
                            />
                          )}
                        {sum > 0 && (
                          <circle
                            cx="36"
                            cy="10"
                            r={
                              minBlobRadius +
                              ((maxBlobRadius - minBlobRadius) * sum) / maxCall
                            }
                            fill="#003366"
                          />
                        )}
                      </svg>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        )}
        {!noData && (
          <tfoot>
            <tr>
              <th scope={"row"}>{t("total")}</th>
              {columnSums.map((sum, index) => (
                <td key={index}>{asEur(-sum, 0, true)}</td>
              ))}
            </tr>
            <tr>
              <th scope={"row"}></th>
              {columnSums.map((sum, index) => (
                <td className={"blobSumCell"} key={index}>
                  <svg
                    className={"blobSumGraphic"}
                    viewBox="-10 0 60 40"
                    fill="none"
                    height={maxSumBarHeight + 10}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width={40}
                      height={(maxSumBarHeight * sum) / maxSum}
                      fill="#003366"
                    />
                  </svg>
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {!noData && <BlobTooltip {...tooltipData} />}
    </div>
  );
};

export default React.memo(BlobTable);
